import React, { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { parseISO, format } from 'date-fns';

import { useUiComponentsInternationalContext } from '@repo/static-translations/src';
import { Button } from '@repo/ui-components/shadcn/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui-components/shadcn/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui-components/shadcn/Select';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@repo/ui-components/shadcn/Alert';
import {
  RadioGroup,
  RadioGroupItem,
} from '@repo/ui-components/shadcn/RadioGroup';
import {
  AppointmentDataProps,
  CustomerLabelEnum,
  DealerProps,
} from './BlockBuilderProspectScheduler.types';

import { pushToDataLayer, removeLeadingZeroFromTime } from './utils';

interface AppointmentDays {
  rawDate: string;
  formattedDate: string;
}
interface AppointmentTimes {
  slotDisplay: string;
  start: string;
  finish?: string;
}

interface AppointmentSelectProps {
  appointmentData: AppointmentDataProps;
  appointmentDays: AppointmentDays[];
  appointmentTimes: AppointmentTimes[];
  appointmentNotAvailable: boolean;
  resetAppointmentAvailability?: () => void;
  loading: boolean;
  selectedDealer: DealerProps;
  handleAppointmentSubmit: () => Promise<void>;
  handleAppointmentTypeChange: (appointmentType: AppointmentDataProps) => void;
  handleDayChange: (day: string) => void;
  handleTimeChange: (time: string) => void;
  appointmentSubmitButtonLabel?: string | null;
}

const AppointmentSelect: React.FC<Readonly<AppointmentSelectProps>> = ({
  appointmentData,
  appointmentDays,
  appointmentTimes,
  appointmentNotAvailable,
  resetAppointmentAvailability,
  loading,
  selectedDealer,
  handleAppointmentSubmit,
  handleAppointmentTypeChange,
  handleDayChange,
  handleTimeChange,
  appointmentSubmitButtonLabel,
}) => {
  const { tt } = useUiComponentsInternationalContext();

  type FormInputs = {
    appointmentDate: string;
    appointmentType:
      | CustomerLabelEnum.E_FENCE
      | CustomerLabelEnum.IN_HOME
      | CustomerLabelEnum.VIRTUAL
      | string;
    appointmentTime: string;
  };

  const form = useForm<FormInputs>({
    defaultValues: {
      appointmentDate:
        appointmentDays.length > 0 ? appointmentDays[0].rawDate : '',
      appointmentType: '',
      appointmentTime:
        appointmentTimes.length > 0 ? appointmentTimes[0].start : '',
    },
  });

  const onSubmit: SubmitHandler<FormInputs> = async (_, event) => {
    event.preventDefault();
    //! this uses some selected slot in outer state reducer to do something instead of passing data....
    await handleAppointmentSubmit();
  };

  const availableAppointmentTypes = appointmentData.workTypes.filter(
    // remove workTypes with no appointment slots
    workType =>
      workType.appointmentSlots &&
      // appointment slots is an object not an array, so check if it has any keys
      Object.keys(workType.appointmentSlots).length > 0
  );

  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);

  useEffect(() => {
    if (availableAppointmentTypes && availableAppointmentTypes.length > 0) {
      const currentValue = form.getValues('appointmentType');
      if (!currentValue) {
        const defaultType =
          availableAppointmentTypes.find(type => type.isDefaultWorkType) ??
          availableAppointmentTypes[0]; // fallback to first if none marked default

        if (defaultType) {
          form.setValue('appointmentType', defaultType.customerLabel);
          handleAppointmentTypeChange({ workTypes: [defaultType] });
        }
      }
    }

    // fire dataLayer event
    pushToDataLayer({
      event: 'prospect_view_calendar',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appointmentDays && appointmentDays.length > 0) {
      const currentDay = form.getValues('appointmentDate');

      // Check if the currentDay is still in the list
      const dayIsValid = appointmentDays.some(
        day => day.rawDate === currentDay
      );

      // If not valid or not set, default to the first available day
      if (!dayIsValid) {
        const firstDay = appointmentDays[0].rawDate;
        form.setValue('appointmentDate', firstDay);
        handleDayChange(firstDay);
      } else {
        handleDayChange(currentDay);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDays, form]);

  // Watch for changes to the appointment times and select the first available time
  useEffect(() => {
    if (appointmentTimes && appointmentTimes.length > 0) {
      const currentTime = form.getValues('appointmentTime');
      const firstTime = appointmentTimes[0].start;
      // Check if the current time is in the list of valid times:
      const isCurrentTimeValid = appointmentTimes.some(
        t => t.start === currentTime
      );

      // If user’s selected time is not valid, reset it to the first time
      if (!isCurrentTimeValid) {
        form.setValue('appointmentTime', firstTime);
        handleTimeChange(firstTime);
      }
    }
  }, [appointmentTimes, form, handleTimeChange]);

  const appointmentTypeMap = {
    [CustomerLabelEnum.E_FENCE]: {
      label: tt(
        s => s.blockBuilderProspectSchedulerComponent.appointmentTypeEfence
      ),
      description: tt(
        s => s.blockBuilderProspectSchedulerComponent.appointmentTypeEfenceLabel
      ),
    },
    [CustomerLabelEnum.IN_HOME]: {
      label: tt(
        s => s.blockBuilderProspectSchedulerComponent.appointmentTypeEfence
      ),
      description: tt(
        s => s.blockBuilderProspectSchedulerComponent.appointmentTypeEfenceLabel
      ),
    },
    [CustomerLabelEnum.VIRTUAL]: {
      label: tt(
        s => s.blockBuilderProspectSchedulerComponent.appointmentTypeVirtual
      ),
      description: tt(
        s =>
          s.blockBuilderProspectSchedulerComponent.appointmentTypeVirtualLabel
      ),
    },
  };

  function formatAppointmentDay(dateString: string): string {
    return format(parseISO(dateString), 'EEEE, MMMM dd, yyyy');
  }

  return (
    <Form {...form}>
      <div className="rounded bg-accent p-4 mb-4 space-y-2">
        <p className="mb-0">
          <strong>
            {tt(
              s => s.blockBuilderProspectSchedulerComponent.yourLocalDealerLabel
            )}
          </strong>
        </p>
        <p className="mb-0">{selectedDealer?.name}</p>
        <p className="mb-0">
          <small>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .dealerSelectServiceAreasLabel
            )}

            {selectedDealer?.mainServiceArea}
          </small>
        </p>
      </div>

      {availableAppointmentTypes.length > 0 && (
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset>
            <FormField
              control={form.control}
              name="appointmentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {tt(
                      s =>
                        s.blockBuilderProspectSchedulerComponent
                          .availableConsultationTypesLabel
                    )}
                  </FormLabel>
                  <FormDescription>
                    <span className="block mb-4">
                      {tt(
                        s =>
                          s.blockBuilderProspectSchedulerComponent
                            .appointmentLengthLabel
                      )}
                    </span>
                  </FormDescription>
                  <FormControl>
                    <RadioGroup
                      className="grid gap-2"
                      onValueChange={value => {
                        field.onChange(value);

                        const selectedType = availableAppointmentTypes.find(
                          type => type.customerLabel === value
                        );

                        if (selectedType) {
                          handleAppointmentTypeChange({
                            workTypes: [selectedType],
                          });
                        }
                        resetAppointmentAvailability?.();
                      }}
                      defaultValue={field.value}
                      value={field.value} // ensure controlled value
                    >
                      {availableAppointmentTypes.map(appointmentType => (
                        <div
                          key={appointmentType.customerLabel}
                          className="mb-2"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                aria-describedby={appointmentType.customerLabel}
                                value={appointmentType.customerLabel}
                              />
                            </FormControl>
                            <FormLabel className="cursor-pointer">
                              {
                                appointmentTypeMap[
                                  appointmentType.customerLabel
                                ]?.label
                              }
                            </FormLabel>
                          </FormItem>
                          <FormDescription
                            id={appointmentType.customerLabel}
                            className="m-0 ml-7"
                          >
                            {
                              appointmentTypeMap[appointmentType.customerLabel]
                                ?.description
                            }
                          </FormDescription>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {appointmentNotAvailable && (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>
                  {tt(
                    s =>
                      s.blockBuilderProspectSchedulerComponent
                        .noAppointmentsAvailableTitle
                  )}
                </AlertTitle>
                <AlertDescription>
                  {tt(
                    s =>
                      s.blockBuilderProspectSchedulerComponent
                        .noAppointmentsAvailableMessage
                  )}
                </AlertDescription>
              </Alert>
            )}

            {appointmentDays && appointmentDays.length > 0 && (
              <>
                <FormField
                  control={form.control}
                  name="appointmentDate"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel htmlFor="appointmentDate-trigger">
                        {tt(
                          s =>
                            s.blockBuilderProspectSchedulerComponent
                              .appointmentDateLabel
                        )}
                      </FormLabel>
                      <Select
                        onValueChange={value => {
                          field.onChange(value);
                          handleDayChange(value);
                          resetAppointmentAvailability?.();
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger
                            id="appointmentDate-trigger"
                            hasError={!!form.formState.errors.appointmentDate}
                            ref={field.ref}
                            onBlur={field.onBlur}
                          >
                            <SelectValue placeholder="Select a day" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {appointmentDays.map(day => (
                            <SelectItem key={day.rawDate} value={day.rawDate}>
                              {formatAppointmentDay(day.rawDate)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {appointmentTimes && appointmentTimes.length > 0 && (
                  <FormField
                    control={form.control}
                    name="appointmentTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="appointmentTime-trigger">
                          {tt(
                            s =>
                              s.blockBuilderProspectSchedulerComponent
                                .appointmentTimeLabel
                          )}
                        </FormLabel>
                        <Select
                          onOpenChange={open => {
                            if (open) {
                              setTimeDropdownOpen(true);
                            } else {
                              // Delay re-enabling to allow UI to settle
                              setTimeout(() => setTimeDropdownOpen(false), 200);
                            }
                          }}
                          onValueChange={value => {
                            field.onChange(value);
                            handleTimeChange(value);
                            resetAppointmentAvailability?.();
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              id="appointmentTime-trigger"
                              hasError={!!form.formState.errors.appointmentTime}
                              ref={field.ref}
                              onBlur={field.onBlur}
                            >
                              <SelectValue placeholder="Select a time" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {appointmentTimes.map(appointment => (
                              <SelectItem
                                key={appointment.slotDisplay}
                                value={appointment.start}
                              >
                                {removeLeadingZeroFromTime(
                                  appointment.slotDisplay
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Touch blocker for iOS Safari */}
                        {timeDropdownOpen && (
                          <div className="fixed inset-0 z-[1600] bg-transparent" />
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
          </fieldset>
          <Button
            className={`mt-4 transition-opacity ${
              timeDropdownOpen && 'pointer-events-none opacity-50 touch-none'
            }`}
            type="submit"
            variant="primary"
            disabled={loading || !form.formState.isValid || timeDropdownOpen}
          >
            {loading
              ? tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent.buttonLoadingText
                )
              : appointmentSubmitButtonLabel ??
                tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent
                      .scheduleAppointmentButton
                )}
          </Button>
        </form>
      )}
    </Form>
  );
};

export default AppointmentSelect;
